// frontend/src/components/client/ClientHangars.jsx
import React, { useState, useEffect } from 'react';
import { Warehouse, Calendar, DollarSign, FileText, TrendingDown, MapPin } from 'lucide-react';
import { getMyHangars } from '../../services/clientApi';

const ProgressBar = ({ percentage, label }) => (
  <div className="mt-2">
    <div className="flex justify-between text-xs text-gray-500 mb-1">
      <span>{label}</span>
      <span>{percentage}%</span>
    </div>
    <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
      <div 
        className="h-full bg-green-500 rounded-full transition-all duration-300"
        style={{ width: `${percentage}%` }}
      />
    </div>
  </div>
);

const ClientHangars = () => {
  const [hangars, setHangars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    loadHangars();
  }, []);

  const loadHangars = async () => {
    setLoading(true);
    try {
      const response = await getMyHangars();
      setHangars(response.data);
    } catch (error) {
      console.error('Error loading hangars:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  return (
    <div className="px-4 sm:px-0">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-white">My Hangars</h2>
        <p className="text-gray-400 text-sm">View your hangar leases and occupancy</p>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : hangars.length === 0 ? (
        <div className="text-center py-12 bg-gray-800/50 rounded-xl border border-gray-700">
          <Warehouse className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500">No active hangar leases</p>
        </div>
      ) : (
        <div className="space-y-4">
          {hangars.map((h) => (
            <div key={h.id} className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
              <div 
                className="p-4 cursor-pointer hover:bg-gray-700/30 transition"
                onClick={() => setExpandedId(expandedId === h.id ? null : h.id)}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-yellow-500/20 rounded-lg">
                      <Warehouse className="w-5 h-5 text-yellow-400" />
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="text-white font-medium">Hangar #{h.hangar_number}</span>
                        <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full">
                          Active
                        </span>
                      </div>
                      <p className="text-sm text-gray-400">{h.hangar_name}</p>
                      <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                        <MapPin className="w-3 h-3" />
                        {h.location || 'No location specified'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-400">Monthly Rent</p>
                    <p className="text-lg font-bold text-white">{formatCurrency(h.monthly_rent)}</p>
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedId === h.id && (
                <div className="p-4 border-t border-gray-700 bg-gray-800/30">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Lease Period
                      </p>
                      <p className="text-white text-sm">
                        {formatDate(h.start_date)} - {formatDate(h.end_date)}
                      </p>
                      {h.days_remaining > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          {h.days_remaining} days remaining
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                        <TrendingDown className="w-3 h-3" />
                        Progress
                      </p>
                      <ProgressBar percentage={h.progress_percentage || 0} label="Lease Progress" />
                    </div>
                  </div>
                  {h.assigned_aircraft_tail && (
                    <div className="mt-3 pt-3 border-t border-gray-700">
                      <p className="text-xs text-gray-500 mb-1">Assigned Aircraft</p>
                      <p className="text-white text-sm">{h.assigned_aircraft_tail}</p>
                    </div>
                  )}
                  <div className="mt-3 pt-3 border-t border-gray-700 flex justify-end">
                    <button className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1">
                      <FileText className="w-4 h-4" />
                      View Contract
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientHangars;